<template>
	<div>
		<div class="t-page-header">
			<div>
				<h1>פרויקטים</h1>
				<div class="t-subtitle">כל הפרויקטים הפעילים בארגון</div>
			</div>
			<button class="t-btn primary" @click="openCreate">+ פרויקט חדש</button>
		</div>

		<div v-if="!projects.length" class="t-empty">
			<div class="t-empty-icon">📁</div>
			אין פרויקטים — צור את הראשון
		</div>

		<div class="t-project-grid">
			<div v-for="p in projects" :key="p.id" class="t-project-card" :style="{'--c': p.color}" @click="$router.push('/tasks/project/' + p.id)">
				<div style="display:flex; justify-content:space-between; align-items:center;">
					<div class="t-avatar" :style="{background: p.color}">{{ icon(p.icon) }}</div>
					<small style="color:var(--t-text-3);">#{{ p.id }}</small>
				</div>
				<h3>{{ p.name }}</h3>
				<div class="t-desc">{{ p.description || "ללא תיאור" }}</div>
				<div class="t-progress"><span :style="{width: progressPct(p) + '%'}"></span></div>
				<div class="t-meta">
					<span>{{ p.done_count || 0 }}/{{ p.tasks_count || 0 }} משימות</span>
					<span>{{ p.owner_name }}</span>
				</div>
			</div>
		</div>

		<!-- create / edit modal -->
		<div v-if="modal.open" class="t-modal-backdrop" @click.self="modal.open = false">
			<div class="t-modal" style="max-width: 540px;">
				<div class="t-modal-head">
					<h3>{{ modal.form.id ? "עריכת פרויקט" : "פרויקט חדש" }}</h3>
					<span class="t-close" @click="modal.open = false">×</span>
				</div>
				<div class="t-modal-body">
					<div class="t-row">
						<div class="t-col">
							<label>שם הפרויקט</label>
							<input v-model="modal.form.name" placeholder="לדוגמה: שיווק Q2" />
						</div>
					</div>
					<div class="t-row">
						<div class="t-col">
							<label>תיאור</label>
							<textarea v-model="modal.form.description" rows="3" placeholder="על מה הפרויקט?"></textarea>
						</div>
					</div>
					<div class="t-row">
						<div class="t-col">
							<label>צבע</label>
							<div style="display:flex; gap:6px; flex-wrap:wrap;">
								<span v-for="c in palette" :key="c"
									@click="modal.form.color = c"
									:style="{background:c, width:'28px', height:'28px', borderRadius:'50%', cursor:'pointer', boxShadow: modal.form.color === c ? '0 0 0 3px rgba(0,0,0,.2)' : 'none'}"></span>
							</div>
						</div>
						<div class="t-col">
							<label>אייקון</label>
							<div style="display:flex; gap:6px; flex-wrap:wrap;">
								<span v-for="ic in icons" :key="ic"
									@click="modal.form.icon = ic"
									:style="{padding:'6px 10px', borderRadius:'8px', cursor:'pointer', border: modal.form.icon === ic ? '2px solid var(--t-primary)' : '1px solid var(--t-border)'}">{{ icon(ic) }}</span>
							</div>
						</div>
					</div>
					<div class="t-row">
						<div class="t-col">
							<label>חברי צוות</label>
							<div style="display:flex; gap:6px; flex-wrap:wrap; margin-top:4px;">
								<span v-for="u in eligibleUsers" :key="u.id"
									@click="toggleMember(u.id)"
									:style="{padding:'6px 12px', borderRadius:'999px', cursor:'pointer', fontSize:'12px', background: modal.form.member_ids.includes(u.id) ? u.color : '#f1f5f9', color: modal.form.member_ids.includes(u.id) ? '#fff' : 'inherit'}">
									{{ u.name }}
								</span>
							</div>
						</div>
					</div>
				</div>
				<div class="t-modal-foot">
					<button v-if="modal.form.id" class="t-btn danger" @click="del">מחק</button>
					<button class="t-btn ghost" @click="modal.open = false">ביטול</button>
					<button class="t-btn primary" @click="save">שמור</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { TasksAPI, TasksRealtime } from "./TasksApi";

const ICON_MAP = { folder: "📁", rocket: "🚀", target: "🎯", chart: "📊", bug: "🐞", design: "🎨", code: "💻", task: "✅" };

export default {
	name: "TasksProjects",
	props: { user: Object, users: Array, projectsList: { type: Array, default: () => [] } },
	emits: ["projects-changed"],
	data() {
		return {
			projects: [],
			palette: ["#6366f1","#ec4899","#f59e0b","#10b981","#3b82f6","#8b5cf6","#ef4444","#14b8a6","#0ea5e9","#84cc16"],
			icons: Object.keys(ICON_MAP),
			modal: { open: false, form: this.emptyForm() },
			subscription: null,
		};
	},
	computed: {
		eligibleUsers() { return (this.users || []).filter(u => u.id !== this.user?.id); },
	},
	watch: {
		projectsList: {
			handler(v) { this.projects = v || []; },
			immediate: true,
		},
	},
	async mounted() {
		const reloadDeb = (() => { let h; return () => { clearTimeout(h); h = setTimeout(() => this.load(), 500); }; })();
		const sub = TasksRealtime.subscribe("tasks-global", {
			"project.updated": reloadDeb,
			"project.deleted": reloadDeb,
		});
		// Use cached projects from parent — only load if missing
		const loads = [sub];
		if (!this.projectsList || !this.projectsList.length) loads.unshift(this.load());
		const results = await Promise.all(loads);
		this.subscription = results[results.length - 1];
	},
	beforeUnmount() {
		if (this.subscription) TasksRealtime.unsubscribe(this.subscription);
	},
	methods: {
		emptyForm() {
			return { id: 0, name: "", description: "", color: "#6366f1", icon: "folder", member_ids: [] };
		},
		icon(k) { return ICON_MAP[k] || "📁"; },
		async load() {
			const res = await TasksAPI.projects();
			this.projects = (res && res.items) || [];
		},
		progressPct(p) {
			const total = +p.tasks_count || 0;
			const done = +p.done_count || 0;
			return total ? Math.round((done / total) * 100) : 0;
		},
		openCreate() {
			this.modal.form = this.emptyForm();
			this.modal.open = true;
		},
		toggleMember(uid) {
			const idx = this.modal.form.member_ids.indexOf(uid);
			if (idx === -1) this.modal.form.member_ids.push(uid);
			else this.modal.form.member_ids.splice(idx, 1);
		},
		async save() {
			if (!this.modal.form.name.trim()) return;
			await TasksAPI.projectSave(this.modal.form);
			this.modal.open = false;
			await this.load();
			this.$emit("projects-changed");
		},
		async del() {
			if (!confirm("למחוק את הפרויקט?")) return;
			await TasksAPI.projectDelete(this.modal.form.id);
			this.modal.open = false;
			await this.load();
			this.$emit("projects-changed");
		},
	},
};
</script>
